import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { getBlogPostPreviewThumbnail } from "@compass/shared/utils";
import { Link, useParams } from "@tanstack/react-router";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { resourcePostRoute } from "app/router";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { UnifiedThreadComment } from "components/common/UnifiedThreadComment";
import { Button, DeleteCommentModal, ProseDocument } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { FavoriteArticlesSidebar } from "components/layout/FavoriteArticlesSidebar";
import { TrendingSidebar } from "components/layout/TrendingSidebar";
import { useCurrentUser } from "hooks/useCurrentUser";
import { formatISODateRelative } from "lib/formatISODate";
import { renderSanitizedHTML } from "lib/renderSanitizedHTML";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";

type CommentEditState = {
    show: boolean;
    item: ApiThreadComment | null;
};

export const ResourcePost: React.FC = () => {
    const { slug } = useParams({ from: resourcePostRoute.id });
    const [isCommentEdit, setIsCommentEdit] = useState<CommentEditState>({
        show: false,
        item: null,
    });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
    const currentUser = useCurrentUser();
    const [emailError, setEmailError] = useState("");

    const { enqueueSnackbar } = useSnackbar();
    const [email, setEmail] = useState("");
    const [recipient, setRecipient] = useState("");
    const [linkModal, setLinkModal] = useState(false);
    const [post] = trpc.resources.getPost.useSuspenseQuery({ slug });
    const { mutateAsync: reactionMutate } = trpc.resources.postReaction.useMutation();
    const { mutate: refreshPreviewStats } = trpc.resources.refreshPreviewStats.useMutation();
    const postId = (post as { id: number }).id;
    const [postReaction] = trpc.resources.getPostReactions.useSuspenseQuery({
        postId,
    });

    // Refresh video preview stats on detail-view mount. Fire-and-forget —
    // we don't block the page on YouTube/Vimeo APIs, and the mutation also
    // writes the results back to the DB so feed cards pick up fresh counts
    // on their next render. Kept before the early-return below so hook
    // order stays stable across renders.
    const resolvedPreview = "error" in post ? null : post.preview;
    const isVideoEmbedRefresh =
        resolvedPreview?.type === "youtube-embed" ||
        resolvedPreview?.type === "vimeo-embed" ||
        resolvedPreview?.type === "video-url" ||
        resolvedPreview?.type === "video-asset";
    useEffect(() => {
        if (!isVideoEmbedRefresh) return;
        refreshPreviewStats({ postId });
    }, [isVideoEmbedRefresh, postId, refreshPreviewStats]);

    // Get or create thread for this blog post
    const [thread] = trpc.resources.getThread.useSuspenseQuery({ slug });

    const queryClient = trpc.useContext();
    const addCommentClearRef = useRef<(() => void) | null>(null);
    const { isPending: isLoading, mutateAsync } = trpc.threads.createComment.useMutation();
    const { mutateAsync: shareMutatePost } = trpc.resources.sharePost.useMutation();
    const { mutateAsync: updateMutateAsync } = trpc.threads.updateComment.useMutation();
    const deleteCommentMutation = trpc.threads.deleteComment.useMutation();
    const [threadComments] = trpc.threads.getComments.useSuspenseQuery({
        threadId: thread.id,
    });

    const { userReaction } = postReaction;

    if ("error" in post) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <h1 className="text-4xl font-bold text-slate-500">404</h1>
                <p className="flex text-2xl">Post not found</p>
                <Link to="/social" className="text-sm underline">
                    Back
                </Link>
            </div>
        );
    }

    const handleAddComment = async (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        try {
            await mutateAsync(
                {
                    threadId: thread.id,
                    contentJson: data.contentJson,
                    isPostedAnonymously: data.isAnonymous,
                    attachmentIds: data.attachmentIds,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Comment added successfully.", { variant: "success" });
                        queryClient.threads.getComments.invalidate();
                        addCommentClearRef.current?.();
                    },
                    onError: console.error,
                },
            );
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeleteClick = (commentId: number) => {
        setCommentToDelete(commentId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteComment = () => {
        if (commentToDelete == null || deleteCommentMutation.isPending) return;
        deleteCommentMutation.mutate(
            { commentId: commentToDelete },
            {
                onSuccess: () => {
                    enqueueSnackbar("Comment deleted successfully.", { variant: "success" });
                    queryClient.threads.getComments.invalidate();
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar("Failed to delete comment.", { variant: "error" });
                },
            },
        );
        setCommentToDelete(null);
    };

    const handleEditComment = async (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        if (!isCommentEdit.item) return;
        try {
            await updateMutateAsync(
                {
                    commentId: isCommentEdit.item.id,
                    contentJson: data.contentJson,
                    isPostedAnonymously: data.isAnonymous,
                    attachmentIds: data.attachmentIds,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Comment edited successfully.", { variant: "success" });
                        queryClient.threads.getComments.invalidate();
                        setIsCommentEdit({ show: false, item: null });
                    },
                    onError: console.error,
                },
            );
        } catch (e) {
            console.error(e);
        }
    };

    const handleShare = async () => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (!email.trim()) {
            setEmailError("Email is required");
            return;
        }

        if (!emailRegex.test(email)) {
            setEmailError("Invalid email address");
            return;
        }

        setEmailError("");

        try {
            await shareMutatePost({ email, recipientName: recipient, slug: post.slug });
            enqueueSnackbar("Shared article", { variant: "success" });
            setLinkModal(false);
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Failed to share article", { variant: "error" });
        }
    };

    const handleReaction = async ({
        id,
        reaction,
    }: {
        id: number;
        reaction: "LIKE" | "UNLIKE";
    }) => {
        try {
            await reactionMutate(
                {
                    postId: id,
                    reaction: reaction,
                },
                {
                    onSuccess: () => {
                        queryClient.resources.getPostReactions.invalidate();
                    },
                },
            );
        } catch (e) {
            console.error(e);
        }
    };

    const previewThumbnail = getBlogPostPreviewThumbnail(post.preview);
    // The server pre-resolves embed-safe URLs (YouTube /embed, Vimeo /player)
    // and exposes them as `preview.embedSrc` — UIs just consume.
    const preview = post.preview;
    const previewType = preview?.type ?? "none";
    const isVideoEmbed = previewType === "youtube-embed" || previewType === "vimeo-embed";
    const isHostedVideo = previewType === "video-url" || previewType === "video-asset";
    // Narrowing through the local `preview` variable: TS picks up that the
    // matched variants all carry `embedSrc` and `url`.
    const isVideoVariant =
        preview != null &&
        (preview.type === "youtube-embed" ||
            preview.type === "vimeo-embed" ||
            preview.type === "video-url" ||
            preview.type === "video-asset");
    const videoEmbedSrc = isVideoVariant ? (preview.embedSrc ?? null) : null;
    // The original URL the author entered — used as the "open in new tab" link.
    const videoUrl = isVideoVariant ? (preview.url ?? null) : null;
    const linkHref: string | null = null;
    const isBlog = post.allCategories.includes("blog");

    // Only the non-interactive image branch is wrapped in the "open in new
    // tab" anchor — embedding interactive media (iframes, <video> with
    // controls, the Apple Podcasts player) inside an <a> is invalid HTML and
    // produces unexpected navigation when users try to play.
    const showThumbnailLink =
        previewThumbnail && !videoEmbedSrc && previewType !== "apple-podcast-embed";

    const mainContent = (
        <>
            {showThumbnailLink && (
                <a
                    href={videoUrl || linkHref || "#"}
                    className="cursor-pointer no-underline outline-none ring-0 visited:outline-none focus:outline-none focus:ring-0 active:outline-none"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div className="relative flex w-full items-center justify-center overflow-hidden rounded-2xl border-x border-t border-[#d4d5d5] bg-black/5">
                        <img
                            src={previewThumbnail}
                            alt={post.title}
                            className="
                                max-h-[240px]
                                w-full
                                rounded-2xl
                                object-contain
                                sm:max-h-[320px]
                                md:max-h-[400px]
                                lg:max-h-[480px]
                            "
                        />
                    </div>
                </a>
            )}
            {videoEmbedSrc && isVideoEmbed && (
                <div className="rounded-t-2xl border border-[#d4d5d5] bg-white p-4">
                    <iframe
                        className="aspect-video w-full rounded-2xl"
                        src={videoEmbedSrc}
                        title={post.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    ></iframe>
                </div>
            )}
            {videoEmbedSrc && isHostedVideo && (
                <div className="rounded-t-2xl border border-[#d4d5d5] bg-white p-4">
                    <video
                        className="w-full rounded-2xl"
                        src={videoEmbedSrc}
                        controls
                        preload="metadata"
                    />
                </div>
            )}
            {preview?.type === "apple-podcast-embed" && preview.embedSrc && (
                <div className="rounded-t-2xl border border-[#d4d5d5] bg-white p-4">
                    <iframe
                        className="w-full rounded-2xl"
                        src={preview.embedSrc}
                        title={post.title}
                        height={preview.kind === "show" ? 450 : 175}
                        allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                        referrerPolicy="strict-origin-when-cross-origin"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="top-0 z-10 -mt-3 rounded-b-2xl border-x border-b border-[#d4d5d5] bg-white p-4">
                <a
                    href={videoUrl || linkHref || "#"}
                    className="cursor-pointer text-inherit no-underline outline-none ring-0 visited:outline-none focus:outline-none focus:ring-0 active:outline-none"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <h1 className="font-sans text-[28px] font-semibold leading-[125%] text-[#424242]">
                        {post.title}
                    </h1>
                </a>
                <div className="flex justify-between">
                    <div>
                        {post.author.id != null ? (
                            <Link
                                to="/author/$id"
                                params={{ id: String(post.author.id) }}
                                className="mr-10 font-sans text-[18px] font-normal leading-[169%] text-[#424242] underline"
                            >
                                {post.author.name}
                            </Link>
                        ) : (
                            <span className="mr-10 font-sans text-[18px] font-normal leading-[169%] text-[#424242]">
                                {post.author.name}
                            </span>
                        )}
                        <span className="font-sans text-[14px] font-normal leading-[100%] text-[#424242]">
                            {formatISODateRelative(post.publishedAt)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 ">
                        <div className="flex flex-row items-center gap-3 rounded bg-[#F8F8F8] px-4 py-2">
                            <div className="flex items-center gap-2">
                                <ThumbsUp
                                    onClick={() =>
                                        handleReaction({
                                            id: post.id,
                                            reaction: "LIKE",
                                        })
                                    }
                                    className="cursor-pointer"
                                    color={
                                        userReaction && userReaction.reaction === "LIKE"
                                            ? "#2da8e0"
                                            : "grey"
                                    }
                                />
                                <div>{postReaction.postLikeCount}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <ThumbsDown
                                    onClick={() =>
                                        handleReaction({
                                            id: post.id,
                                            reaction: "UNLIKE",
                                        })
                                    }
                                    className="cursor-pointer"
                                    color={
                                        userReaction && userReaction.reaction === "UNLIKE"
                                            ? "#2da8e0"
                                            : "grey"
                                    }
                                />
                                <div>{postReaction.postDislikeCount}</div>
                            </div>
                            <FavoriteButton target={{ type: "blogPost", id: postId }} size={18} />
                        </div>
                        <button
                            onClick={() => {
                                setEmail("");
                                setLinkModal(true);
                            }}
                            className="rounded-lg bg-gray-100 px-4 py-2"
                        >
                            Share
                        </button>
                    </div>
                </div>
                <div className="mt-6 [&_.blog-embed-video]:rounded-lg [&_.blog-embed-vimeo]:aspect-video [&_.blog-embed-youtube]:aspect-video [&_.blog-embed]:my-4 [&_.blog-embed]:w-full">
                    <ProseDocument doc={post.contentJson} expanded />
                </div>
            </div>
            <div className="mt-6 rounded-2xl border border-[#d4d5d5] bg-white p-4">
                <ThreadCommentForm
                    context="blog"
                    onSubmit={handleAddComment}
                    isLoading={isLoading}
                    placeholder="Add comment..."
                    submitLabel="Submit"
                    showUserIdentity
                    componentType="blog"
                    clearFormRef={addCommentClearRef}
                />
            </div>
            {threadComments.length > 0 && (
                <div className="mt-6 rounded-2xl border border-[#d4d5d5] bg-white p-4">
                    <h2 className="flex items-center gap-2 font-sans text-[16px] font-bold leading-normal tracking-normal text-[#424242]">
                        Comments
                        {threadComments.length > 0 && (
                            <span className="ml-1 rounded-md bg-[#2CA4E3] px-2 py-0.5 text-[12px] font-bold text-white">
                                {threadComments.length}
                            </span>
                        )}
                    </h2>
                    <div className="mt-4 space-y-4">
                        {threadComments.map((comment) => (
                            <UnifiedThreadComment
                                key={comment.id}
                                comment={comment}
                                context="blog"
                                currentUser={currentUser}
                                onEdit={() => setIsCommentEdit({ show: true, item: comment })}
                                onDelete={() => handleDeleteClick(comment.id)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className="relative min-h-screen">
            <DeleteCommentModal
                isOpen={isDeleteModalOpen}
                setIsOpen={setIsDeleteModalOpen}
                onDelete={confirmDeleteComment}
            />
            {isCommentEdit.show && isCommentEdit.item && (
                <div className="fixed inset-0 z-30 bg-slate-900/20">
                    <div className="fixed left-1/2 top-32 z-40 w-[400px] -translate-x-1/2 rounded bg-white p-4">
                        <ThreadCommentForm
                            context="blog"
                            mode="edit"
                            initialContentJson={isCommentEdit.item.contentJson}
                            initialAttachments={isCommentEdit.item.attachments}
                            initialIsAnonymous={isCommentEdit.item.isPostedAnonymously}
                            onSubmit={handleEditComment}
                            onCancel={() => setIsCommentEdit({ show: false, item: null })}
                            showCancel
                            isLoading={isLoading}
                            submitLabel="Edit Comment"
                            placeholder="Write your comment here..."
                        />
                    </div>
                </div>
            )}
            <div className="mx-auto flex min-h-screen w-full flex-col py-6">
                {isBlog ? (
                    <div className="grid grid-cols-12 gap-6">
                        <div className="col-span-12 md:col-span-8">
                            <div className="mb-3 font-sans text-[28px] font-semibold text-[#424242]">
                                Articles
                            </div>
                            <div className="rounded-2xl border border-[#c8c8c8] bg-[#ffffff] p-4">
                                <a
                                    href={videoUrl || linkHref || "#"}
                                    className="cursor-pointer no-underline outline-none ring-0 visited:outline-none focus:outline-none focus:ring-0 active:outline-none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <h1 className="mb-4 font-sans text-[28px] font-semibold leading-[125%] text-[#424242]">
                                        {post.title}
                                    </h1>

                                    {previewThumbnail && (
                                        <div className="mb-4 w-full rounded-2xl">
                                            <img
                                                src={previewThumbnail}
                                                alt={post.title}
                                                className="h-full w-full rounded-2xl object-cover"
                                            />
                                        </div>
                                    )}
                                    {videoEmbedSrc && isVideoEmbed && (
                                        <div className="rounded-t-2xl border border-[#d4d5d5] bg-white p-4">
                                            <iframe
                                                className="aspect-video w-full rounded-2xl"
                                                src={videoEmbedSrc}
                                                title={post.title}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                    {videoEmbedSrc && isHostedVideo && (
                                        <div className="rounded-t-2xl border border-[#d4d5d5] bg-white p-4">
                                            <video
                                                className="w-full rounded-2xl"
                                                src={videoEmbedSrc}
                                                controls
                                                preload="metadata"
                                            />
                                        </div>
                                    )}

                                    <div className="line-clamp-3 font-sans text-[16px] font-normal leading-[1.69] text-[#424242]">
                                        {post.excerptJson && (
                                            <ProseDocument doc={post.excerptJson} expanded />
                                        )}
                                    </div>
                                </a>

                                <div className="flex justify-between">
                                    <div>
                                        <span className="mr-10 font-sans text-[18px] font-normal leading-[169%] text-[#424242]">
                                            {post.author.name}
                                        </span>
                                        <span className="font-sans text-[14px] font-normal leading-[100%] text-[#424242]">
                                            {formatISODateRelative(post.publishedAt)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 ">
                                        <div className="flex flex-row items-center gap-3 rounded bg-[#F8F8F8] px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <ThumbsUp
                                                    onClick={() =>
                                                        handleReaction({
                                                            id: post.id,
                                                            reaction: "LIKE",
                                                        })
                                                    }
                                                    className="cursor-pointer"
                                                    color={
                                                        userReaction &&
                                                        userReaction.reaction === "LIKE"
                                                            ? "#2da8e0"
                                                            : "grey"
                                                    }
                                                />
                                                <div>{postReaction.postLikeCount}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <ThumbsDown
                                                    onClick={() =>
                                                        handleReaction({
                                                            id: post.id,
                                                            reaction: "UNLIKE",
                                                        })
                                                    }
                                                    className="cursor-pointer"
                                                    color={
                                                        userReaction &&
                                                        userReaction.reaction === "UNLIKE"
                                                            ? "#2da8e0"
                                                            : "grey"
                                                    }
                                                />
                                                <div>{postReaction.postDislikeCount}</div>
                                            </div>
                                            <FavoriteButton
                                                target={{ type: "blogPost", id: postId }}
                                                size={18}
                                            />
                                        </div>
                                        <button
                                            onClick={() => {
                                                setEmail("");
                                                setLinkModal(true);
                                            }}
                                            className="rounded-lg bg-gray-100 px-4 py-2"
                                        >
                                            Share
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 rounded-2xl border border-[#d4d5d5] bg-white p-4">
                                <ThreadCommentForm
                                    context="blog"
                                    onSubmit={handleAddComment}
                                    isLoading={isLoading}
                                    placeholder="Add comment..."
                                    submitLabel="Submit"
                                    showUserIdentity
                                    componentType="blog"
                                    clearFormRef={addCommentClearRef}
                                />
                            </div>
                            {threadComments.length > 0 && (
                                <div className="mt-6 rounded-2xl border border-[#d4d5d5] bg-white p-4">
                                    <h2 className="flex items-center gap-2 font-sans text-[16px] font-bold leading-normal tracking-normal text-[#424242]">
                                        Comments
                                        {threadComments.length > 0 && (
                                            <span className="ml-1 rounded-md bg-[#2CA4E3] px-2 py-0.5 text-[12px] font-bold text-white">
                                                {threadComments.length}
                                            </span>
                                        )}
                                    </h2>
                                    <div className="mt-4 space-y-4">
                                        {threadComments.map((comment) => (
                                            <UnifiedThreadComment
                                                key={comment.id}
                                                comment={comment}
                                                context="blog"
                                                currentUser={currentUser}
                                                onEdit={() =>
                                                    setIsCommentEdit({ show: true, item: comment })
                                                }
                                                onDelete={() => handleDeleteClick(comment.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="hidden lg:col-span-4 lg:block">
                            <TrendingSidebar
                                filterType="blogPost"
                                title="Trending Articles"
                                limit={20}
                            />
                            <div className="mt-4" />
                            <FavoriteArticlesSidebar />
                        </div>
                    </div>
                ) : (
                    mainContent
                )}
            </div>
            {linkModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-4">
                        <h2 className="mb-3 font-sans text-[24px] font-bold text-[#424242]">
                            Share Link
                        </h2>
                        <hr className="-mx-[16px] mb-5 border-[#c8c8c8]" />

                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Enter Email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                }}
                                className={`w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 outline-none ${
                                    emailError
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-blue-500"
                                }`}
                            />
                            {emailError && (
                                <p className="mt-1 text-sm text-red-500">{emailError}</p>
                            )}
                        </div>

                        <input
                            type="text"
                            placeholder="Enter Recipient Name"
                            value={recipient}
                            onChange={(e) => setRecipient(e.target.value)}
                            className="mb-4 w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 outline-none"
                        />
                        <div className="flex justify-end">
                            <Button
                                onClick={() => {
                                    handleShare();
                                }}
                                variant="primary"
                            >
                                Share <SubmitIcon className="ml-2" />
                            </Button>
                        </div>
                        <button
                            className="absolute right-4 top-4"
                            onClick={() => setLinkModal(false)}
                        >
                            <CloseIcon className="h-[30px] w-[30px]" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
